import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import type { Ticket } from '@overlayqa/shared';
import { VerificationCard } from '../../routes/issues/VerificationCard';
import type { IssueVerificationApi } from './useIssueVerification';

/**
 * The issue page's "Fix verification" section: history load state, the
 * verification card, and the extension fallback after a failed server capture.
 * Extracted from IssueDetailPage so the page stays composition-only
 * (docs/refactoring/2026-06-03-ticket-detail-page.md).
 */
export function IssueVerificationDetails({ ticket, verification, readOnly = false }: { readOnly?: boolean; ticket: Ticket; verification: IssueVerificationApi }) {
  const {
    latestVerification,
    verificationHistory,
    historyState,
    onRetryHistory,
    inProgress,
    serverCaptureFailed,
    onVerify,
    onVerifyViaExtension,
    onMarkVerified,
    onReopen,
    onSkip,
    statusActionPending,
  } = verification;

  return (
    <details key={ticket.id} id="issue-verification" className="card-sm group oq-verification-section">
      <summary className="section-label cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center gap-2">
        Fix verification
        <HugeiconsIcon icon={ArrowDown01Icon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      {historyState === 'loading' && !inProgress && <p className="mt-4 text-sm text-gray-500" role="status">Loading verification history...</p>}
      {historyState === 'error' && !inProgress && (
        <div className="mt-4 text-sm" role="alert">
          <p>Couldn’t load verification history.{latestVerification ? ' Showing the last loaded result.' : ''}</p>
          <button type="button" className="btn-ghost mt-2" onClick={onRetryHistory}><span className="text-cap-trim">Retry history</span></button>
        </div>
      )}
      {(historyState === 'ready' || latestVerification || inProgress) && <VerificationCard
        embedded
        readOnly={readOnly}
        issueId={ticket.id}
        verification={latestVerification}
        history={verificationHistory}
        beforeScreenshotUrl={ticket.screenshotUrl}
        inProgress={inProgress ?? undefined}
        onVerify={onVerify}
        onMarkVerified={onMarkVerified}
        onReopen={onReopen}
        onRetry={onVerify}
        onSkip={onSkip}
        statusActionPending={statusActionPending}
      />}
      {serverCaptureFailed && !readOnly && (
        <aside className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Couldn&apos;t load this page from our servers. It may require a login or be a
          local/private URL.{' '}
          <button
            type="button"
            onClick={onVerifyViaExtension}
            className="font-medium underline hover:text-amber-900"
          >
            <span className="text-cap-trim block">Verify with the extension</span>
          </button>
        </aside>
      )}
    </details>
  );
}
